import api from '../utils/api';

const franchiseService = {
    /**
     * Submit a new franchise request
     * @param {Object} data - { name, email, phone, businessName, message }
     * @returns {Promise} - Response data
     */
    createRequest: async (data) => {
        try {
            const response = await api.post('/franchise/request', data);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Get all tables for the logged-in franchise
     * @returns {Promise}
     */
    getTables: async () => {
        try {
            const response = await api.get('/franchise/tables');
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Add a new table
     * @param {Object} data - { tableNumber }
     * @returns {Promise}
     */
    addTable: async (data) => {
        try {
            const response = await api.post('/franchise/tables', data);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Get menu items for the logged-in franchise
     * @returns {Promise}
     */
    getMenuItems: async () => {
        try {
            const response = await api.get('/franchise/menu/items');
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Get menu categories for the logged-in franchise
     * @returns {Promise}
     */
    getCategories: async () => {
        try {
            const response = await api.get('/franchise/menu/categories');
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Create a new menu item
     * @param {Object} data - { name, categoryId, price, description, imageUrl, isAvailable }
     * @returns {Promise}
     */
    createMenuItem: async (data) => {
        try {
            const response = await api.post('/franchise/menu/items', data);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Update a menu item
     * @param {number} id - Menu item ID
     * @param {Object} data - { name, categoryId, price, description, imageUrl, isAvailable }
     * @returns {Promise}
     */
    updateMenuItem: async (id, data) => {
        try {
            const response = await api.put(`/franchise/menu/items/${id}`, data);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Delete a menu item
     * @param {number} id - Menu item ID
     * @returns {Promise}
     */
    deleteMenuItem: async (id) => {
        try {
            const response = await api.delete(`/franchise/menu/items/${id}`);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Create a new menu category
     * @param {Object} data - { name, displayOrder }
     * @returns {Promise}
     */
    createCategory: async (data) => {
        try {
            const response = await api.post('/franchise/menu/categories', data);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Delete a table
     * @param {number} id - Table ID
     * @returns {Promise}
     */
    deleteTable: async (id) => {
        try {
            const response = await api.delete(`/franchise/tables/${id}`);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Get live orders for the franchise
     * @returns {Promise}
     */
    getLiveOrders: async () => {
        try {
            const response = await api.get('/franchise/orders/live');
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Update order status
     * @param {number} id - Order ID
     * @param {string} status - New status
     * @returns {Promise}
     */
    updateOrderStatus: async (id, status) => {
        try {
            const response = await api.put(`/orders/${id}/status`, { status });
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Mark order as viewed
     * @param {number} id - Order ID
     * @returns {Promise}
     */
    markOrderAsViewed: async (id) => {
        try {
            const response = await api.put(`/orders/${id}/viewed`);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Get order by table ID (active order only)
     * @param {number} tableId - Table ID
     * @returns {Promise}
     */
    getOrderByTable: async (tableId) => {
        try {
            const response = await api.get(`/orders/table/${tableId}`);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },

    /**
     * Get all orders for a table (order history)
     * @param {number} tableId - Table ID
     * @returns {Promise}
     */
    getAllOrdersByTable: async (tableId) => {
        try {
            const response = await api.get(`/orders/table/${tableId}/all`);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    },
    /**
     * Request more tags for the franchise
     * @param {Object} data - { bikeCount, carCount, message }
     * @returns {Promise}
     */
    requestTags: async (data) => {
        try {
            const response = await api.post('/franchise/tags/request', data);
            return response.data;
        } catch (error) {
            throw error.response ? error.response.data : { message: 'Network error details not available' };
        }
    }
};

export default franchiseService;
